// hooks/use-pagination.ts
import { useMemo } from 'react';

interface UsePaginationProps {
    totalItems: number;
    itemsPerPage: number;
    currentPage: number;
    siblingCount?: number;
}

export const usePagination = ({ totalItems, itemsPerPage, currentPage, siblingCount = 1 }: UsePaginationProps) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const range = (start: number, end: number) => {
        const length = end - start + 1;
        return Array.from({ length }, (_, idx) => idx + start);
    };

    const getPageNumbers = () => {
        const totalPageNumbers = siblingCount + 5;

        if (totalPages <= totalPageNumbers) {
            return range(1, totalPages);
        }

        const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
        const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

        const shouldShowLeftEllipsis = leftSiblingIndex > 2;
        const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 2;

        if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
            const leftItemCount = 3 + 2 * siblingCount;
            return [...range(1, leftItemCount), '...', totalPages];
        }

        if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
            const rightItemCount = 3 + 2 * siblingCount;
            return [1, '...', ...range(totalPages - rightItemCount + 1, totalPages)];
        }

        return [1, '...', ...range(leftSiblingIndex, rightSiblingIndex), '...', totalPages];
    };

    return useMemo(
        () => ({
            totalPages,
            getPageNumbers,
        }),
        [totalPages, currentPage, siblingCount],
    );
};
